import React, { useEffect, useRef, useState } from 'react';
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  type BusinessDay,
  type CandlestickData,
  type HistogramData,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import { Paper, Typography, CircularProgress, Box } from '@mui/material';
import type { Exchange as CcxtExchange } from 'ccxt';

interface TradingChartProps {
  exchange: string;
  base: string;
  quote: string;
  isFutures?: boolean;
}

type CcxtModule = typeof import('ccxt');
type ExchangeConstructor = new (params?: Record<string, unknown>) => CcxtExchange;

const CHART_HEIGHT = 400;
const LOOKBACK_MINUTES = 300;
const MAX_CANDLES = 500;
const DEFAULT_TIMEFRAME = '1m';
const REFRESH_INTERVAL_MS = 60_000;
const MS_IN_MINUTE = 60_000;
const DEFAULT_TIMEZONE = 'Asia/Seoul';
const MAX_PRICE_FRACTION_DIGITS = 12;
const DEFAULT_VISIBLE_MINUTES = 120;
const MAX_VOLUME_FRACTION_DIGITS = 12;
const MIN_VOLUME_FRACTION_DIGITS = 2;

type TradeLike = {
  id?: string | number;
  timestamp?: number;
  datetime?: string;
  price?: number;
  amount?: number;
  cost?: number;
};

type AggregatedBar = {
  minute: number;
  open: number;
  high: number;
  low: number;
  close: number;
  baseVolume: number;
  quoteVolume: number;
};

type VolumePoint = HistogramData & { baseVolume: number; quoteVolume: number };

type CcxtModuleWithPro = CcxtModule & { pro?: Record<string, unknown> };

let ccxtModulePromise: Promise<{ module: CcxtModuleWithPro; usingPro: boolean }> | null = null;

async function loadCcxtModule(): Promise<{ module: CcxtModuleWithPro; usingPro: boolean }> {
  if (!ccxtModulePromise) {
    ccxtModulePromise = import('ccxt').then((raw) => {
      const resolved = (raw.default ?? raw) as CcxtModuleWithPro;
      const hasProNamespace = resolved.pro && typeof resolved.pro === 'object';
      return {
        module: resolved,
        usingPro: Boolean(hasProNamespace),
      };
    });
  }
  return ccxtModulePromise;
}

function extractExchangeCtor(registry: Record<string, unknown> | undefined, exchangeId: string): ExchangeConstructor | null {
  if (!registry) {
    return null;
  }
  const candidate = registry[exchangeId];
  if (typeof candidate === 'function') {
    return candidate as ExchangeConstructor;
  }
  return null;
}

function isValidCandle(
  entry: number[] | undefined,
): entry is [number, number, number, number, number, number] {
  if (!entry || entry.length < 6) {
    return false;
  }
  return entry.slice(0, 6).every((value) => typeof value === 'number' && Number.isFinite(value));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function minuteFloor(timestamp: number): number {
  return Math.floor(timestamp / MS_IN_MINUTE) * MS_IN_MINUTE;
}

function toUtcTime(minute: number): UTCTimestamp {
  return (minute / 1000) as UTCTimestamp;
}

function toSeconds(time: Time): number | null {
  if (typeof time === 'number') {
    return time;
  }
  if (typeof time === 'string') {
    const parsed = Number(time);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (time && typeof time === 'object' && 'year' in time) {
    const date = toDateFromBusinessDay(time as BusinessDay);
    return Math.floor(date.getTime() / 1000);
  }
  return null;
}

function extractNumeric(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function countDecimalPlaces(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const absValue = Math.abs(value);
  if (absValue === 0) {
    return 0;
  }
  const textual = absValue.toString();
  if (!textual.includes('e') && !textual.includes('E')) {
    const [, fraction = ''] = textual.split('.');
    return Math.min(fraction.length, MAX_PRICE_FRACTION_DIGITS);
  }
  const [mantissa, exponentPart] = textual.split('e');
  const exponent = Number(exponentPart);
  if (!Number.isFinite(exponent)) {
    return 0;
  }
  const [, mantissaFraction = ''] = mantissa.split('.');
  const sanitizedMantissaFraction = mantissaFraction.replace(/0+$/, '');
  if (exponent >= 0) {
    const decimals = sanitizedMantissaFraction.length - exponent;
    return Math.min(Math.max(decimals, 0), MAX_PRICE_FRACTION_DIGITS);
  }
  const decimals = sanitizedMantissaFraction.length + Math.abs(exponent);
  return Math.min(decimals, MAX_PRICE_FRACTION_DIGITS);
}

function normalizeTrade(trade: TradeLike | null | undefined): {
  timestamp: number;
  price: number;
  amount: number;
  id?: string | number;
} | null {
  if (!trade || typeof trade !== 'object') {
    return null;
  }

  const rawTimestamp =
    typeof trade.timestamp === 'number' && Number.isFinite(trade.timestamp)
      ? trade.timestamp
      : typeof trade.datetime === 'string'
        ? Date.parse(trade.datetime)
        : null;
  const timestamp = rawTimestamp && Number.isFinite(rawTimestamp) ? rawTimestamp : Date.now();

  const rawPrice = trade.price;
  const price = typeof rawPrice === 'number' && Number.isFinite(rawPrice) ? rawPrice : NaN;

  const rawAmount =
    typeof trade.amount === 'number' && Number.isFinite(trade.amount)
      ? trade.amount
      : typeof trade.cost === 'number' && Number.isFinite(trade.cost) && Number.isFinite(price) && price !== 0
        ? trade.cost / price
        : NaN;

  if (!Number.isFinite(price) || !Number.isFinite(rawAmount)) {
    return null;
  }

  return {
    timestamp,
    price,
    amount: Math.abs(rawAmount),
    id: trade.id,
  };
}

function aggregationToCandle(agg: AggregatedBar): CandlestickData {
  return {
    time: toUtcTime(agg.minute),
    open: agg.open,
    high: agg.high,
    low: agg.low,
    close: agg.close,
  };
}

function aggregationToVolume(agg: AggregatedBar): VolumePoint {
  return {
    time: toUtcTime(agg.minute),
    value: Math.max(agg.baseVolume, 0),
    color: agg.close >= agg.open ? '#26a69a' : '#ef5350',
    baseVolume: Math.max(agg.baseVolume, 0),
    quoteVolume: Math.max(agg.quoteVolume, 0),
  };
}

function resolveTimeZone(): string {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return resolved || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

function toDateFromBusinessDay(day: BusinessDay): Date {
  return new Date(Date.UTC(day.year, day.month - 1, day.day));
}

function createTimeFormatter(timeZone: string): (time: Time) => string {
  const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const dayFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    month: 'short',
    day: 'numeric',
  });
  return (time) => {
    if (typeof time === 'object' && time !== null) {
      return dayFormatter.format(toDateFromBusinessDay(time as BusinessDay));
    }
    const seconds = typeof time === 'number' ? time : Number(time);
    return dateTimeFormatter.format(new Date(seconds * 1000));
  };
}

function createTickFormatter(timeZone: string): (time: UTCTimestamp) => string {
  const tickFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
  });
  const dayFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    month: 'short',
    day: 'numeric',
  });
  const analysisFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return (time) => {
    const date = new Date(time * 1000);
    const parts = analysisFormatter.formatToParts(date);
    const hourPart = parts.find((part) => part.type === 'hour')?.value ?? '00';
    const minutePart = parts.find((part) => part.type === 'minute')?.value ?? '00';
    const hour = Number.parseInt(hourPart, 10);
    const minute = Number.parseInt(minutePart, 10);
    if (!Number.isNaN(hour) && !Number.isNaN(minute) && hour === 0 && minute === 0) {
      return dayFormatter.format(date);
    }
    return tickFormatter.format(date);
  };
}

function clampSeries(
  candleSeries: CandlestickData[],
  volumeSeries: VolumePoint[],
): void {
  if (candleSeries.length <= MAX_CANDLES) {
    return;
  }
  const excess = candleSeries.length - MAX_CANDLES;
  candleSeries.splice(0, excess);
  volumeSeries.splice(0, excess);
}

function upsertBar(
  candleSeries: CandlestickData[],
  volumeSeries: VolumePoint[],
  candle: CandlestickData,
  volume: VolumePoint,
): void {
  const lastIndex = candleSeries.length - 1;
  if (lastIndex >= 0) {
    const last = candleSeries[lastIndex];
    if (last.time === candle.time) {
      candleSeries[lastIndex] = candle;
      volumeSeries[lastIndex] = volume;
      return;
    }
    if (last.time < candle.time) {
      candleSeries.push(candle);
      volumeSeries.push(volume);
      clampSeries(candleSeries, volumeSeries);
      return;
    }
  }

  const existingIndex = candleSeries.findIndex((entry) => entry.time === candle.time);
  if (existingIndex >= 0) {
    candleSeries[existingIndex] = candle;
    volumeSeries[existingIndex] = volume;
    return;
  }

  let insertIndex = 0;
  while (insertIndex < candleSeries.length && candleSeries[insertIndex].time < candle.time) {
    insertIndex += 1;
  }
  candleSeries.splice(insertIndex, 0, candle);
  volumeSeries.splice(insertIndex, 0, volume);
  clampSeries(candleSeries, volumeSeries);
}

const TradingChart: React.FC<TradingChartProps> = ({ exchange, base, quote, isFutures = false }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) {
      setError('Unable to initialize chart canvas.');
      setIsLoading(false);
      return undefined;
    }

    const chart = createChart(container, {
      width: container.clientWidth || undefined,
      height: CHART_HEIGHT,
      layout: {
        background: { color: 'transparent' },
        textColor: 'rgba(255, 255, 255, 0.9)',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: 'rgba(197, 203, 206, 0.2)' },
        horzLines: { color: 'rgba(197, 203, 206, 0.2)' },
      },
      timeScale: { timeVisible: true, secondsVisible: false },
    });
    const resolvedTimeZone = resolveTimeZone();
    const timeFormatter = createTimeFormatter(resolvedTimeZone);
    const tickFormatter = createTickFormatter(resolvedTimeZone);
    chart.applyOptions({
      localization: {
        timeFormatter,
      },
    });
    chart.timeScale().applyOptions({
      timeVisible: true,
      secondsVisible: false,
      tickMarkFormatter: tickFormatter,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderDownColor: '#ef5350',
      borderUpColor: '#26a69a',
      wickDownColor: '#ef5350',
      wickUpColor: '#26a69a',
    });
    candleSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.05, bottom: 0.25 },
    });
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      priceLineVisible: false,
    });
    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    let maxPriceFractionDigits = 0;
    let minPriceIncrement: number | null = null;
    let lastAppliedPriceDigits: number | null = null;
    let lastAppliedPriceMinMove: number | null = null;
    let maxVolumeFractionDigits = 0;
    let lastAppliedVolumeDigits: number | null = null;
    let lastAppliedVolumeMinMove: number | null = null;

    const pricePrecisionKeys = [
      'priceScale',
      'price_precision',
      'pricePrecision',
      'quote_precision',
      'quotePrecision',
    ];
    const priceIncrementKeys = [
      'tickSize',
      'tick_size',
      'minTickSize',
      'priceIncrement',
      'price_increment',
      'price_tick',
      'priceTick',
      'minPriceIncrement',
      'min_price_increment',
      'quoteIncrement',
      'quote_increment',
    ];

    const volumePrecisionKeys = [
      'amount_precision',
      'amountPrecision',
      'base_precision',
      'basePrecision',
      'quantity_precision',
      'quantityPrecision',
      'lot_precision',
      'lotPrecision',
    ];
    const volumeIncrementKeys = [
      'amountIncrement',
      'amount_increment',
      'quantityIncrement',
      'quantity_increment',
      'stepSize',
      'step_size',
      'lotSize',
      'lot_size',
      'baseMinSize',
      'base_min_size',
      'minQuantity',
      'min_quantity',
    ];


    const collectCandidates = (
      source: Record<string, unknown> | undefined,
      keys: string[],
      handler: (value: unknown) => void,
    ) => {
      if (!source) {
        return;
      }
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          handler(source[key]);
        }
      }
    };

    const applyPriceFormat = () => {
      const digitsFromIncrement =
        minPriceIncrement !== null ? countDecimalPlaces(minPriceIncrement) : 0;
      const digits = Math.min(
        MAX_PRICE_FRACTION_DIGITS,
        Math.max(maxPriceFractionDigits, digitsFromIncrement),
      );
      const impliedMinMove =
        digits > 0 ? Number(Math.pow(10, -digits).toFixed(digits)) : 1;
      const minMove = minPriceIncrement !== null ? minPriceIncrement : impliedMinMove;
      const sameDigits = lastAppliedPriceDigits === digits;
      const sameMinMove =
        lastAppliedPriceMinMove !== null &&
        Math.abs(lastAppliedPriceMinMove - minMove) <=
          Number.EPSILON * Math.max(1, Math.abs(minMove));
      if (sameDigits && sameMinMove) {
        return;
      }

      lastAppliedPriceDigits = digits;
      lastAppliedPriceMinMove = minMove;

      const numberFormatter = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      });
      const priceFormatter = (price: number): string => numberFormatter.format(price);

      candleSeries.applyOptions({
        priceFormat: {
          type: 'custom',
          minMove,
          formatter: priceFormatter,
        },
      });
      chart.applyOptions({
        localization: {
          timeFormatter,
          priceFormatter,
        },
      });
    };

    const quoteFormatter = new Intl.NumberFormat(undefined, {
      notation: 'compact',
      maximumFractionDigits: 2,
    });

    const applyVolumeFormat = () => {
      const digits = Math.min(
        MAX_VOLUME_FRACTION_DIGITS,
        Math.max(maxVolumeFractionDigits, MIN_VOLUME_FRACTION_DIGITS),
      );
      const minMove = digits > 0 ? Number(Math.pow(10, -digits).toFixed(digits)) : 1;
      const sameDigits = lastAppliedVolumeDigits === digits;
      const sameMinMove =
        lastAppliedVolumeMinMove !== null &&
        Math.abs(lastAppliedVolumeMinMove - minMove) <=
          Number.EPSILON * Math.max(1, Math.abs(minMove));
      if (sameDigits && sameMinMove) {
        return;
      }

      lastAppliedVolumeDigits = digits;
      lastAppliedVolumeMinMove = minMove;

      const fractionDigits = digits > 0 ? digits : 0;
      const baseFormatter = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
      const volumeFormatter = (value: number): string => {
        const formattedBase = baseFormatter.format(value);
        const baseLabel = `${formattedBase} ${upperBase}`;
        if (!Number.isFinite(lastKnownClose) || lastKnownClose <= 0) {
          return baseLabel;
        }
        const quoteValue = value * lastKnownClose;
        const formattedQuote = quoteFormatter.format(Math.max(quoteValue, 0));
        return `${baseLabel} (${formattedQuote} ${upperQuote})`;
      };

      volumeSeries.applyOptions({
        priceFormat: {
          type: 'custom',
          minMove,
          formatter: volumeFormatter,
        },
      });
    };

    const recordPricePrecisionDigitsCandidate = (candidate: unknown) => {
      const numeric = extractNumeric(candidate);
      if (numeric === null || numeric < 0) {
        return;
      }
      const normalized = Math.min(
        MAX_PRICE_FRACTION_DIGITS,
        Math.floor(numeric),
      );
      if (normalized > maxPriceFractionDigits) {
        maxPriceFractionDigits = normalized;
        applyPriceFormat();
      }
    };

    const recordPriceValuePrecision = (price: number) => {
      if (!Number.isFinite(price)) {
        return;
      }
      const decimals = countDecimalPlaces(price);
      if (decimals > maxPriceFractionDigits) {
        maxPriceFractionDigits = decimals;
        applyPriceFormat();
      }
    };

    const recordPriceIncrementCandidate = (candidate: unknown) => {
      const numeric = extractNumeric(candidate);
      if (numeric === null || numeric <= 0) {
        return;
      }
      if (minPriceIncrement === null || numeric < minPriceIncrement) {
        minPriceIncrement = numeric;
        applyPriceFormat();
      } else {
        const decimals = countDecimalPlaces(numeric);
        if (decimals > maxPriceFractionDigits) {
          maxPriceFractionDigits = decimals;
          applyPriceFormat();
        }
      }
    };

    const recordVolumePrecisionDigitsCandidate = (candidate: unknown) => {
      const numeric = extractNumeric(candidate);
      if (numeric === null || numeric < 0) {
        return;
      }
      const normalized = Math.min(
        MAX_VOLUME_FRACTION_DIGITS,
        Math.floor(numeric),
      );
      if (normalized > maxVolumeFractionDigits) {
        maxVolumeFractionDigits = normalized;
        applyVolumeFormat();
      }
    };

    const recordVolumeValuePrecision = (volume: number) => {
      if (!Number.isFinite(volume)) {
        return;
      }
      const decimals = countDecimalPlaces(volume);
      if (decimals > maxVolumeFractionDigits) {
        maxVolumeFractionDigits = decimals;
        applyVolumeFormat();
      }
    };

    const recordVolumeIncrementCandidate = (candidate: unknown) => {
      const numeric = extractNumeric(candidate);
      if (numeric === null || numeric <= 0) {
        return;
      }
      const decimals = countDecimalPlaces(numeric);
      if (decimals > maxVolumeFractionDigits) {
        maxVolumeFractionDigits = decimals;
        applyVolumeFormat();
      }
    };

    const processMarketMetadata = (metadata: Record<string, unknown>) => {
      const precisionField = metadata['precision'];
      if (typeof precisionField === 'number') {
        recordPricePrecisionDigitsCandidate(precisionField);
      } else if (precisionField && typeof precisionField === 'object') {
        const precisionRecord = precisionField as Record<string, unknown>;
        if ('price' in precisionRecord) {
          recordPricePrecisionDigitsCandidate(precisionRecord['price']);
        }
        if ('amount' in precisionRecord) {
          recordVolumePrecisionDigitsCandidate(precisionRecord['amount']);
        }
        if ('base' in precisionRecord) {
          recordVolumePrecisionDigitsCandidate(precisionRecord['base']);
        }
        if ('volume' in precisionRecord) {
          recordVolumePrecisionDigitsCandidate(precisionRecord['volume']);
        }
      }

      collectCandidates(metadata, pricePrecisionKeys, recordPricePrecisionDigitsCandidate);
      collectCandidates(metadata, priceIncrementKeys, recordPriceIncrementCandidate);
      collectCandidates(metadata, volumePrecisionKeys, recordVolumePrecisionDigitsCandidate);
      collectCandidates(metadata, volumeIncrementKeys, recordVolumeIncrementCandidate);

      const info = metadata['info'];
      if (info && typeof info === 'object') {
        const infoRecord = info as Record<string, unknown>;
        collectCandidates(infoRecord, pricePrecisionKeys, recordPricePrecisionDigitsCandidate);
        collectCandidates(infoRecord, priceIncrementKeys, recordPriceIncrementCandidate);
        collectCandidates(infoRecord, volumePrecisionKeys, recordVolumePrecisionDigitsCandidate);
        collectCandidates(infoRecord, volumeIncrementKeys, recordVolumeIncrementCandidate);
      }

      const limits = metadata['limits'];
      if (limits && typeof limits === 'object') {
        const amountLimits = (limits as Record<string, unknown>)['amount'];
        if (amountLimits && typeof amountLimits === 'object') {
          const amountRecord = amountLimits as Record<string, unknown>;
          collectCandidates(amountRecord, volumePrecisionKeys, recordVolumePrecisionDigitsCandidate);
          collectCandidates(amountRecord, volumeIncrementKeys, recordVolumeIncrementCandidate);
          if ('min' in amountRecord) {
            const minCandidate = extractNumeric(amountRecord['min']);
            if (minCandidate !== null) {
              recordVolumeValuePrecision(minCandidate);
            }
          }
          if ('max' in amountRecord) {
            const maxCandidate = extractNumeric(amountRecord['max']);
            if (maxCandidate !== null) {
              recordVolumeValuePrecision(maxCandidate);
            }
          }
        }
      }
    };
    const upperBase = (base ?? '').toUpperCase();
    const upperQuote = (quote ?? '').toUpperCase();

    applyPriceFormat();
    applyVolumeFormat();
    const symbol = `${upperBase}/${upperQuote}`;
    const normalizedExchange = exchange.toLowerCase();
    const supportsNativeStream = normalizedExchange === 'upbit' || normalizedExchange === 'bithumb';
    const shouldUseCustomTrades = supportsNativeStream && (upperQuote === 'KRW' || upperQuote === 'USDT');

    let isActive = true;
    let exchangeInstance: CcxtExchange | null = null;
    let marketsLoaded = false;
    let usingPro = false;
    let marketMetadata: Record<string, unknown> | null = null;
    const candleBuffer: CandlestickData[] = [];
    let customTradeWs: WebSocket | null = null;
    let customReconnectTimer: number | null = null;
    let customReconnectDelay = 1000;
    const volumeBuffer: VolumePoint[] = [];
    let lastKnownClose = Number.NaN;
    let currentAggregation: AggregatedBar | null = null;
    let watchLoopStarted = false;
    let lastProcessedTimestamp = 0;
    let lastProcessedId: string | number | null = null;
    let lastProcessedPrice = 0;
    let lastProcessedAmount = 0;
    let initialViewApplied = false;

    const applyInitialVisibleRange = () => {
      if (initialViewApplied || candleBuffer.length === 0) {
        return;
      }
      const firstSeconds = toSeconds(candleBuffer[0].time);
      const lastSeconds = toSeconds(candleBuffer[candleBuffer.length - 1].time);
      if (firstSeconds === null || lastSeconds === null) {
        chart.timeScale().fitContent();
        initialViewApplied = true;
        return;
      }
      const range = DEFAULT_VISIBLE_MINUTES * 60;
      const fromSeconds = Math.max(firstSeconds, lastSeconds - range);
      chart.timeScale().setVisibleRange({
        from: fromSeconds as UTCTimestamp,
        to: lastSeconds as UTCTimestamp,
      });
      initialViewApplied = true;
    };

    const clearCustomReconnect = () => {
      if (customReconnectTimer !== null) {
        window.clearTimeout(customReconnectTimer);
        customReconnectTimer = null;
      }
    };

    const closeCustomStream = (reason?: string) => {
      if (customTradeWs) {
        try {
          customTradeWs.close();
        } catch (closeError) {
          console.warn('[TradingChart] Failed to close custom trade stream', closeError);
        }
        customTradeWs = null;
      }
      if (reason) {
        console.warn(`[TradingChart] Custom trade stream closed: ${reason}`);
      }
      clearCustomReconnect();
      customReconnectDelay = 1000;
    };

    const scheduleCustomReconnect = () => {
      if (!shouldUseCustomTrades) {
        return;
      }
      if (customTradeWs || customReconnectTimer !== null) {
        return;
      }
      const delay = customReconnectDelay;
      customReconnectTimer = window.setTimeout(() => {
        customReconnectTimer = null;
        connectCustomStream(true);
      }, delay);
      customReconnectDelay = Math.min(customReconnectDelay * 2, 30_000);
    };

    const connectCustomStream = (isReconnect = false) => {
      if (!shouldUseCustomTrades || customTradeWs) {
        return;
      }
      if (!base || !quote) {
        return;
      }

      const endpoint = normalizedExchange === 'upbit'
        ? 'wss://api.upbit.com/websocket/v1'
        : 'wss://ws-api.bithumb.com/websocket/v1';

      try {
        if (isReconnect) {
          console.info('[TradingChart] Reconnecting custom trade stream...');
        }
        const ws = new WebSocket(endpoint);
        customTradeWs = ws;
        ws.binaryType = 'arraybuffer';

        const code = `${upperQuote}-${upperBase}`;

        ws.onopen = () => {
          customReconnectDelay = 1000;
          clearCustomReconnect();

          const subscription = [
            { ticket: 'trading-chart' },
            { type: 'trade', codes: [code] },
            { format: 'SIMPLE' },
          ];

          try {
            ws.send(JSON.stringify(subscription));
          } catch (sendError) {
            console.error('[TradingChart] Failed to subscribe to custom trade stream', sendError);
          }
        };

        ws.onmessage = (event: MessageEvent) => {
          const processMessage = async () => {
            try {
              let payload: unknown;
              if (event.data instanceof ArrayBuffer) {
                payload = new TextDecoder().decode(event.data);
              } else if (event.data instanceof Blob) {
                payload = await event.data.text();
              } else {
                payload = event.data;
              }

              if (typeof payload !== 'string') {
                return;
              }

              const parsed = JSON.parse(payload) as Record<string, unknown>;
              const messageType = typeof parsed.ty === 'string' ? parsed.ty.toLowerCase() : '';
              if (messageType !== 'trade' && messageType !== 'transaction') {
                return;
              }
              const tradeCode = typeof parsed.cd === 'string' ? parsed.cd : null;
              if (!tradeCode || tradeCode.toUpperCase() !== code) {
                return;
              }

              const price = typeof parsed.tp === 'number' ? parsed.tp : Number(parsed.tp);
              if (!Number.isFinite(price)) {
                return;
              }

              const rawVolume = typeof parsed.tv === 'number'
                ? parsed.tv
                : typeof parsed.volume === 'number'
                  ? parsed.volume
                  : Number(parsed.ttv);
              const amount = Number.isFinite(rawVolume) ? Math.abs(rawVolume) : 0;

              const rawTimestamp =
                typeof parsed.tms === 'number'
                  ? parsed.tms
                  : typeof parsed.ts === 'number'
                    ? parsed.ts
                    : typeof parsed.ttm === 'number'
                      ? parsed.ttm
                      : Date.now();

              const timestamp =
                rawTimestamp > 1e12 ? rawTimestamp : Math.floor(rawTimestamp * 1000);

              handleNormalizedTrade({
                timestamp,
                price,
                amount,
                id: `${normalizedExchange}:${timestamp}:${price}:${amount}`,
              });
            } catch (streamError) {
              console.error('[TradingChart] Custom trade stream parse error', streamError);
            }
          };

          void processMessage();
        };

        ws.onerror = (err) => {
          console.error('[TradingChart] Custom trade stream error', err);
        };

        ws.onclose = (evt) => {
          customTradeWs = null;
          if (!isActive) {
            return;
          }
          console.warn(
            `[TradingChart] Custom trade stream closed (${evt.code} ${evt.reason || ''})`,
          );
          scheduleCustomReconnect();
        };
      } catch (streamError) {
        console.error('[TradingChart] Custom trade stream connection error', streamError);
        scheduleCustomReconnect();
      }
    };

    let lastCorrectedMinute: number | null = null;

    const resetSeries = () => {
      candleBuffer.length = 0;
      volumeBuffer.length = 0;
      currentAggregation = null;
      candleSeries.setData([]);
      volumeSeries.setData([]);
      lastKnownClose = Number.NaN;
      initialViewApplied = false;
    };

    const applyOhlcv = (entries: number[][]) => {
      for (const entry of entries) {
        if (!isValidCandle(entry)) {
          continue;
        }
        const [time, open, high, low, close, volume] = entry;
        recordPriceValuePrecision(open);
        recordPriceValuePrecision(high);
        recordPriceValuePrecision(low);
        recordPriceValuePrecision(close);
        const baseVolume = Math.max(volume, 0);
        recordVolumeValuePrecision(baseVolume);
        const minute = minuteFloor(time);
        if (Number.isFinite(close)) {
          lastKnownClose = close;
        }
        const candle: CandlestickData = {
          time: toUtcTime(minute),
          open,
          high,
          low,
          close,
        };
        const volumePoint: VolumePoint = {
          time: toUtcTime(minute),
          value: baseVolume,
          color: close >= open ? '#26a69a' : '#ef5350',
          baseVolume,
          quoteVolume:
            Number.isFinite(lastKnownClose) && lastKnownClose > 0
              ? Math.max(baseVolume * lastKnownClose, 0)
              : baseVolume,
        };
        upsertBar(candleBuffer, volumeBuffer, candle, volumePoint);
      }
    };

    const syncSeries = (applyInitialRange = false) => {
      candleSeries.setData([...candleBuffer]);
      volumeSeries.setData([...volumeBuffer]);
      if (applyInitialRange) {
        applyInitialVisibleRange();
      }
    };

    const refreshAggregationFromBuffers = () => {
      if (!currentAggregation) {
        return;
      }
      const minute = currentAggregation.minute;
      const idx = candleBuffer.findIndex((entry) => entry.time === toUtcTime(minute));
      if (idx === -1) {
        currentAggregation = null;
        return;
      }
      const candle = candleBuffer[idx];
      if (Number.isFinite(candle.close)) {
        lastKnownClose = candle.close;
      }
      const volume = volumeBuffer[idx];
      currentAggregation = {
        minute,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        baseVolume: Math.max(volume?.baseVolume ?? volume?.value ?? 0, 0),
        quoteVolume: Math.max(volume?.quoteVolume ?? (volume?.value ?? 0) * (Number.isFinite(candle.close) ? candle.close : 0), 0),
      };
    };

    const applyAggregationToBuffers = () => {
      if (!currentAggregation) {
        return;
      }
      const candle = aggregationToCandle(currentAggregation);
      const volumePoint = aggregationToVolume(currentAggregation);
      upsertBar(candleBuffer, volumeBuffer, candle, volumePoint);
    };

    const correctClosedMinute = async (minute: number) => {
      if (!exchangeInstance) {
        return;
      }
      try {
        const since = Math.max(0, minute - MS_IN_MINUTE);
        const limit = 3;
        const corrected = await exchangeInstance.fetchOHLCV(
          symbol,
          DEFAULT_TIMEFRAME,
          since,
          limit,
        );
        if (!isActive) {
          return;
        }
        applyOhlcv(corrected);
        refreshAggregationFromBuffers();
        if (currentAggregation) {
          applyAggregationToBuffers();
        }
        syncSeries();
      } catch (err) {
        if (isActive) {
          console.error('Failed to correct closed minute', err);
        }
      }
    };

    const handleNormalizedTrade = (normalized: {
      timestamp: number;
      price: number;
      amount: number;
      id?: string | number;
    }) => {
      recordPriceValuePrecision(normalized.price);
      const minuteStart = minuteFloor(normalized.timestamp);

      if (currentAggregation && minuteStart > currentAggregation.minute) {
        const completedMinute = currentAggregation.minute;
        applyAggregationToBuffers();
        syncSeries();
        if (lastCorrectedMinute !== completedMinute) {
          lastCorrectedMinute = completedMinute;
          void correctClosedMinute(completedMinute);
        }
        currentAggregation = null;
      }

      if (!currentAggregation || currentAggregation.minute !== minuteStart) {
        const idx = candleBuffer.findIndex((entry) => entry.time === toUtcTime(minuteStart));
        if (idx >= 0) {
          const existingCandle = candleBuffer[idx];
          const existingVolumePoint = volumeBuffer[idx];
          currentAggregation = {
            minute: minuteStart,
            open: existingCandle.open,
            high: existingCandle.high,
            low: existingCandle.low,
            close: existingCandle.close,
            baseVolume: Math.max(existingVolumePoint?.baseVolume ?? existingVolumePoint?.value ?? 0, 0),
            quoteVolume: Math.max(existingVolumePoint?.quoteVolume ?? 0, 0),
          };
        } else {
          currentAggregation = {
            minute: minuteStart,
            open: normalized.price,
            high: normalized.price,
            low: normalized.price,
            close: normalized.price,
            baseVolume: 0,
            quoteVolume: 0,
          };
        }
      }

      currentAggregation.high = Math.max(currentAggregation.high, normalized.price);
      currentAggregation.low = Math.min(currentAggregation.low, normalized.price);
      currentAggregation.close = normalized.price;
      if (!Number.isFinite(currentAggregation.open)) {
        currentAggregation.open = normalized.price;
      }
      const safeIncrement = Math.max(normalized.amount, 0);
      currentAggregation.baseVolume += safeIncrement;
      const quoteIncrement = normalized.price * safeIncrement;
      if (Number.isFinite(quoteIncrement)) {
        currentAggregation.quoteVolume += Math.max(quoteIncrement, 0);
      }
      recordVolumeValuePrecision(safeIncrement);
      recordVolumeValuePrecision(currentAggregation.baseVolume);
      if (Number.isFinite(normalized.price) && normalized.price > 0) {
        lastKnownClose = normalized.price;
      }

      applyAggregationToBuffers();
      syncSeries();

      lastProcessedTimestamp = normalized.timestamp;
      lastProcessedId = normalized.id ?? null;
      lastProcessedPrice = normalized.price;
      lastProcessedAmount = normalized.amount;
    };

    const startWatchLoop = () => {
      if (watchLoopStarted) {
        return;
      }
      if (shouldUseCustomTrades) {
        watchLoopStarted = true;
        return;
      }
      const supportsBatch =
        !!exchangeInstance?.has?.watchTradesForSymbols &&
        typeof (exchangeInstance as unknown as Record<string, unknown>).watchTradesForSymbols ===
          'function';
      const supportsSingle =
        !!exchangeInstance?.has?.watchTrades &&
        typeof (exchangeInstance as unknown as Record<string, unknown>).watchTrades === 'function';

      if (!supportsBatch && !supportsSingle) {
        if (usingPro) {
          console.warn(`[TradingChart] ${exchange} does not expose websocket trades in ccxt.pro build.`);
        } else {
          console.warn(
            `[TradingChart] ${exchange} websocket streaming requires ccxt.pro; continuing with REST polling only.`,
          );
        }
        watchLoopStarted = true;
        return;
      }

      watchLoopStarted = true;

      (async () => {
        while (isActive) {
          try {
            if (!exchangeInstance) {
              break;
            }

            let payload: unknown;
            if (supportsBatch) {
              const response = await (exchangeInstance as unknown as {
                watchTradesForSymbols: (
                  symbols: string[],
                ) => Promise<Record<string, unknown> | unknown[]>;
              }).watchTradesForSymbols([symbol]);
              if (response && typeof response === 'object' && symbol in response) {
                payload = (response as Record<string, unknown>)[symbol];
              } else {
                payload = response;
              }
            } else if (supportsSingle) {
              payload = await (exchangeInstance as unknown as {
                watchTrades: (symbol: string) => Promise<unknown[] | unknown>;
              }).watchTrades(symbol);
            } else {
              break;
            }

            const tradesArray = Array.isArray(payload) ? payload : payload ? [payload] : [];
            for (const trade of tradesArray) {
              const normalized = normalizeTrade(trade as TradeLike);
              if (!normalized) {
                continue;
              }
              if (
                normalized.timestamp < lastProcessedTimestamp ||
                (normalized.timestamp === lastProcessedTimestamp &&
                  ((normalized.id ?? null) === lastProcessedId ||
                    ((normalized.id == null && lastProcessedId == null) &&
                      normalized.price === lastProcessedPrice &&
                      normalized.amount === lastProcessedAmount)))
              ) {
                continue;
              }

              handleNormalizedTrade(normalized);
            }
          } catch (err) {
            if (!isActive) {
              break;
            }
            console.error('Failed to watch trades', err);
            await sleep(2_000);
          }
        }
      })().catch((err) => {
        console.error('watchTrades loop aborted', err);
      });
    };

    const fetchData = async (useSpinner = true) => {
      if (!isActive) {
        return;
      }

      if (useSpinner) {
        setIsLoading(true);
      }
      setError(null);

      if (!exchange || !base || !quote) {
        resetSeries();
        setError('Missing exchange or trading pair information.');
        if (useSpinner) {
          setIsLoading(false);
        }
        return;
      }

      try {
        if (!exchangeInstance) {
          const { module: ccxtModule, usingPro: loadedUsingPro } = await loadCcxtModule();
          const normalizedExchange = exchange.toLowerCase();
          const supported = Array.isArray(ccxtModule.exchanges) ? ccxtModule.exchanges : [];

          if (!supported.includes(normalizedExchange)) {
            throw new Error(`Exchange '${exchange}' is not supported by ccxt.`);
          }

          const proNamespace = ccxtModule.pro && typeof ccxtModule.pro === 'object'
            ? (ccxtModule.pro as Record<string, unknown>)
            : undefined;
          const proCtor = extractExchangeCtor(proNamespace, normalizedExchange);
          const fallbackCtor = extractExchangeCtor(
            ccxtModule as unknown as Record<string, unknown>,
            normalizedExchange,
          );
          const ExchangeCtor = proCtor ?? fallbackCtor;

          if (!ExchangeCtor) {
            throw new Error(`Failed to load exchange '${exchange}' from ccxt bundle.`);
          }

          usingPro = Boolean(proCtor);

          const instanceOptions: Record<string, unknown> = {
            enableRateLimit: true,
            timeout: 15_000,
          };
          if (usingPro) {
            instanceOptions.newUpdates = false;
          }
          // Set options for futures trading
          if (isFutures) {
            instanceOptions.options = { defaultType: 'future' };
          }

          exchangeInstance = new ExchangeCtor(instanceOptions);
          if (!exchangeInstance.has || !exchangeInstance.has.fetchOHLCV) {
            throw new Error(`${exchange} does not expose OHLCV data through ccxt.`);
          }
        }

        if (!exchangeInstance) {
          throw new Error(`Failed to initialize exchange client for ${exchange}.`);
        }

        if (!marketsLoaded) {
          await exchangeInstance.loadMarkets();
          marketsLoaded = true;
        }
        if (!marketMetadata) {
          try {
            const marketLookup = exchangeInstance as unknown as {
              market?: (symbol: string) => unknown;
            };
            if (typeof marketLookup.market === 'function') {
              const maybeMarket = marketLookup.market(symbol);
              if (maybeMarket && typeof maybeMarket === 'object') {
                marketMetadata = maybeMarket as Record<string, unknown>;
              }
            }
          } catch (metadataError) {
            console.warn(
              `[TradingChart] Failed to resolve market metadata for ${symbol}`,
              metadataError,
            );
          }
        }
        if (marketMetadata) {
          processMarketMetadata(marketMetadata);
        }

        const since = exchangeInstance.milliseconds() - LOOKBACK_MINUTES * MS_IN_MINUTE;
        const limit = Math.max(1, Math.min(LOOKBACK_MINUTES, MAX_CANDLES));
        const rawOhlcv = await exchangeInstance.fetchOHLCV(
          symbol,
          DEFAULT_TIMEFRAME,
          since,
          limit,
        );

        if (!isActive) {
          return;
        }

        const hadData = candleBuffer.length > 0;
        candleBuffer.length = 0;
        volumeBuffer.length = 0;
        applyOhlcv(rawOhlcv);
        refreshAggregationFromBuffers();
        if (currentAggregation) {
          applyAggregationToBuffers();
        }
        syncSeries(!hadData);

        if (shouldUseCustomTrades) {
          connectCustomStream();
        } else if (!watchLoopStarted) {
          startWatchLoop();
        }
      } catch (e) {
        if (!isActive) {
          return;
        }
        console.error(e);
        const message =
          e instanceof Error ? e.message : 'Failed to fetch candlestick data from ccxt.';
        resetSeries();
        setError(message);
      } finally {
        if (isActive && useSpinner) {
          setIsLoading(false);
        }
        if (isActive) {
          scheduleNextRefresh();
        }
      }
    };

    void fetchData(true);

    let refreshId: number | null = null;

    const scheduleNextRefresh = () => {
      if (refreshId !== null) {
        window.clearTimeout(refreshId);
      }
      const now = exchangeInstance?.milliseconds?.() ?? Date.now();
      const millisecondsIntoMinute = now % MS_IN_MINUTE;
      const delay = millisecondsIntoMinute === 0 ? REFRESH_INTERVAL_MS : MS_IN_MINUTE - millisecondsIntoMinute;
      refreshId = window.setTimeout(() => {
        void fetchData(false);
      }, delay);
    };

    const updateSize = () => {
      if (!chartContainerRef.current) {
        return;
      }
      const { clientWidth } = chartContainerRef.current;
      chart.resize(clientWidth || undefined, CHART_HEIGHT);
    };

    updateSize();

    let resizeObserver: ResizeObserver | null = null;
    let windowResizeBound = false;

    if (typeof ResizeObserver !== 'undefined' && chartContainerRef.current) {
      resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(chartContainerRef.current);
    } else {
      windowResizeBound = true;
      window.addEventListener('resize', updateSize);
    }

    return () => {
      isActive = false;
      if (refreshId !== null) {
        window.clearTimeout(refreshId);
      }
      resizeObserver?.disconnect();
      if (windowResizeBound) {
        window.removeEventListener('resize', updateSize);
      }
      closeCustomStream();
      if (exchangeInstance && typeof exchangeInstance.close === 'function') {
        void exchangeInstance.close();
      }
      chart.remove();
    };
  }, [exchange, base, quote, isFutures]);


  return (
    <Paper
      elevation={4}
      sx={{
        p: 2,
        borderRadius: 3,
        background: 'linear-gradient(180deg, rgba(15,18,28,0.9) 0%, rgba(9,12,20,0.95) 100%)',
        border: '1px solid rgba(255,255,255,0.05)',
        minHeight: CHART_HEIGHT + 56,
        display: 'flex',
        flexDirection: 'column',
        color: 'rgba(255, 255, 255, 0.7)',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
        1-Minute Chart
      </Typography>
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        <div
          ref={chartContainerRef}
          style={{
            flex: 1,
            width: '100%',
            height: '100%',
            opacity: error ? 0.1 : 1,
            transition: 'opacity 0.2s ease',
          }}
        />
        {!error && (
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 36, md: 40 },
              left: 12,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: 'rgba(17, 24, 39, 0.85)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
            }}
          >
            VOLUME
          </Box>
        )}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backdropFilter: 'blur(2px)',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && !isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              px: 2,
            }}
          >
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default TradingChart;
